/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState, useCallback, useMemo } from 'react';
import {
  shiftAnchor,
  type AgendaView,
} from '@features/agenda/utils/agenda-dates';

/**
 * Local UI state for the agenda page: view, anchor date, and filters.
 * @param calendarIds - Calendar ids available for filtering.
 * @param memberEmails - Member emails available for filtering.
 * @param initialView - The initial agenda view.
 * @returns The agenda view/date/filter state and setters.
 */
export const useAgendaState = (
  calendarIds: string[],
  memberEmails: string[] = [],
  initialView: AgendaView = 'week'
) => {
  const [view, setView] = useState<AgendaView>(initialView);
  const [anchor, setAnchor] = useState<Date>(() => new Date());
  const [hidden, setHidden] = useState<Set<string>>(() => new Set());
  const [hiddenMembers, setHiddenMembers] = useState<Set<string>>(
    () => new Set(memberEmails)
  );

  const navigate = useCallback(
    (direction: 1 | -1) =>
      setAnchor((prev) => shiftAnchor(view, prev, direction)),
    [view]
  );

  const goToday = useCallback(() => setAnchor(new Date()), []);

  const goToDay = useCallback((date: Date) => {
    setAnchor(date);
    setView('day');
  }, []);

  const toggleCalendar = useCallback((calendarId: string) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(calendarId)) {
        next.delete(calendarId);
      } else {
        next.add(calendarId);
      }
      return next;
    });
  }, []);

  const toggleMember = useCallback((email: string) => {
    setHiddenMembers((prev) => {
      const next = new Set(prev);
      if (next.has(email)) {
        next.delete(email);
      } else {
        next.add(email);
      }
      return next;
    });
  }, []);

  const activeCalendars = useMemo(
    () => new Set(calendarIds.filter((id) => !hidden.has(id))),
    [calendarIds, hidden]
  );

  const activeMembers = useMemo(
    () => new Set(memberEmails.filter((e) => !hiddenMembers.has(e))),
    [memberEmails, hiddenMembers]
  );

  return {
    view,
    setView,
    anchor,
    navigate,
    goToday,
    goToDay,
    activeCalendars,
    toggleCalendar,
    activeMembers,
    toggleMember,
  };
};
